import { DefaultTheme, makeStyles } from "@material-ui/styles";
import { FC, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Loader } from "../../../components/loader";
import { LoyaltyLevel } from "../../../components/loyalty/utils";
import { Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { BillingDTO } from "../../../store/billing/models/dto";
import {
  additionalFeatures,
  BillableApi,
} from "../../../store/billing/models/internal";
import { Billing } from "../../../store/billing/reducer";
import { useFetchable } from "../../../store/common/fetchable";
import { UsersDTO } from "../../../store/users/models/dto";
import { Users } from "../../../store/users/reducer";
import { device } from "../../../styles/media";
import { toDecimal } from "../../../utils/utils";

type TableEntry = Partial<
  Record<typeof LoyaltyLevel.T | "feature" | "NONE", number | string>
>;

const useStyles = makeStyles<DefaultTheme, { selectedColumn: number }>({
  root: ({ selectedColumn }) => ({
    height: "100%",
    overflow: "auto",
    fontSize: "0.8em",

    [`& .MuiTable-root .MuiTableRow-root:nth-child(2n+1) .MuiTableCell-body:nth-child(${selectedColumn})`]: {
      background: "rgb(168 225 255)",
    },

    [`& .MuiTable-root .MuiTableRow-root:nth-child(2n) .MuiTableCell-body:nth-child(${selectedColumn}), .MuiTable-root .MuiTableCell-head:nth-child(${selectedColumn})`]: {
      background: "rgb(200 233 251)",
    },
    [`@media ${device.laptop}`]: {
      fontSize: "1em",
    },
  }),
});

export const PriceList: FC<{}> = () => {
  const pricing = useFetchable(Billing.pricing);
  const currentUser = useFetchable(Users.current);
  const { t } = useTranslation("billing");

  return (
    <>
      <Loader<
        BillingDTO.Responses.GetPricing.T | UsersDTO.Responses.GetSingle.T
      >
        data={[pricing, currentUser]}
      >
        {([loadedPricing, loadedUser]) => (
          <InternalPriceList
            pricing={loadedPricing as BillingDTO.Responses.GetPricing.T}
            user={loadedUser as UsersDTO.Responses.GetSingle.T}
          />
        )}
      </Loader>
      <div>
        <div>
          <small>
            {t("believer-status-gives-you-discount", { amount: 5 })}
          </small>
        </div>
        <div>
          <small>(*) {t("only-available-as-an-additional-feature")}</small>
        </div>
      </div>
    </>
  );
};

const InternalPriceList: FunctionComponent<{
  pricing: BillingDTO.Responses.GetPricing.T;
  user: UsersDTO.Responses.GetSingle.T;
}> = ({ pricing, user }) => {
  const tableHead: TableHeaders<keyof TableEntry> = [
    {
      name: "feature",
      title: "Feature",
    },
    ...pricing.levelDiscounts
      .filter(
        (x) =>
          x.userLevel !== "BELIEVER" &&
          (x.userLevel === "NONE" ||
            user.roleDetails.roles.includes("ROLE_CRYPTO"))
      )
      .map((x) => ({
        title:
          x.userLevel === "NONE"
            ? user.roleDetails.roles.includes("ROLE_CRYPTO")
              ? "Credits"
              : ""
            : x.userLevel,
        name: x.userLevel,
        centered: true,
      })),
  ];

  const tableData: Array<Partial<TableEntry>> = pricing.featureDetails
    .filter(({ originalPrice }) => originalPrice > 0)
    .map(({ featureName: apiName, originalPrice }) => ({
      feature:
        BillableApi.enum[apiName] +
        (additionalFeatures.includes(apiName) ? " *" : ""),
      ...pricing.levelDiscounts.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.userLevel]: toDecimal(originalPrice * (1 - curr.discountRate)),
        }),
        {}
      ),
    }));

  const selectedColumn =
    tableHead.findIndex((x) => x.name === (user.level || "NONE")) + 1;

  const { root } = useStyles({ selectedColumn });

  return (
    <div className={root}>
      <Table<
        TableEntry,
        keyof TableEntry,
        typeof LoyaltyLevel.T | "feature" | "NONE"
      >
        data={tableData}
        head={tableHead}
        unsortable={[...LoyaltyLevel.keys, "feature", "NONE"]}
      ></Table>
    </div>
  );
};
